import useAuth from '../use-auth';
import chmln from '@chamaeleonidae/chmln';
import { isEmpty } from 'lodash-es';
import { useEffect, useRef } from 'react';
import {
  UserPurposeEnum,
  UserTypeEnum,
  useGetAdoptionAccountsIdLazyQuery,
} from '@lon/shared/requests';
import { useGetActiveContentGroupsWithSchoolsQuery } from '@lon/shared/requests';
import { User } from '@lon/shared/types';

const chamToken =
  'SGPRRK1wZofLL2xQRuzHYXPsxwsV4QER7IszrQTPnYJwYI-1P30gL-E4F628ltWCPuSy2R';

const initChameleon = ({
  user,
  contentGroups,
  role,
  version,
  adoptionAccountId = '',
}: {
  user: User;
  contentGroups: string[];
  role: string;
  version: 'MathNation' | 'STEMscopes 4.0';
  adoptionAccountId?: string;
}) => {
  chmln.init(chamToken, { fastUrl: 'https://fast.chameleon.io/' });

  chmln.identify(user?.userId || '', {
    email: user?.email || '',
    name: `${user?.firstName} ${user?.lastName}`,
    role: role,
    product: contentGroups.join(','),
    version,
    adoptionAccountId,
    district: user?.districtId || '',
    state: user?.state || '',
  });
};

const useChameleon = (version: 'MathNation' | 'STEMscopes 4.0') => {
  const chameleonRef = useRef(false);
  const { data, loading: contentGroupsLoading } =
    useGetActiveContentGroupsWithSchoolsQuery();
  const [{ user }] = useAuth();
  const [fetchAdoptionAccounts] = useGetAdoptionAccountsIdLazyQuery();

  const contentGroups =
    data?.activeContentGroupsWithSchools[0]?.contentGroups || [];

  useEffect(() => {
    if (isEmpty(user) || contentGroupsLoading) {
      return;
    }

    let role = '';

    if (
      user?.type === UserTypeEnum.Parent ||
      user?.type === UserTypeEnum.Student
    ) {
      const type = user.type;
      role = type.charAt(0).toUpperCase() + type.slice(1);
    } else {
      user?.userRoles
        ?.filter((role) => !!role.code)
        ?.forEach((r) => {
          role = role += r.code;
        });
    }

    if (!chameleonRef.current && !user.impersonated && !!role) {
      chameleonRef.current = true;

      if (user?.purpose === UserPurposeEnum.AdoptionReviewer) {
        fetchAdoptionAccounts({
          variables: {
            filter: {
              districtId: user?.districtId,
            },
          },
          onCompleted: (data) => {
            initChameleon({
              user,
              contentGroups,
              role,
              version,
              adoptionAccountId: data?.adoptionAccounts.collection[0].id,
            });
          },
        });
      } else {
        initChameleon({
          user,
          contentGroups,
          role,
          version,
        });
      }
    }
  }, [contentGroups]);
};

export default useChameleon;
